import { Box, Button, styled, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { CircleAlert } from "lucide-react";
import { ButtonProps, ButtonPropsRestyle } from "./Button.types";
import { Icon } from "../Icon";

export const ButtonText: ButtonProps = {
  children: "text1",
  variant: "contained"
};

export const ButtonBookmark: ButtonProps = {
  $icon: true,
  children: <Icon icon={ "Icons_salva-preferito_glow" } width={ "24px" } height={ "24px" } />,
  color: "primary",
  variant: "outlined"
};

export const ButtonLink: ButtonProps = {
  $icon: true,
  children: ["text", <Icon icon={ "left" } size={ 24 } key={ "icons" } />],
  color: "primary",
  variant: "text"
};

type TypographySizeProps = Pick<ButtonPropsRestyle, "buttonSize">;

export const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "buttonSize"
})<TypographySizeProps>(
  ({ buttonSize = "md" }) => ({
    fontFamily: "Inter",
    fontSize: buttonSize === "lg" ? "1.25rem" : "0.875rem",
    fontStyle: buttonSize === "lg" ? "bold" : "normal",
    fontWeight: buttonSize === "lg" ? "700" : "500",
    lineHeight: buttonSize === "lg" ? "normal" : "1.5rem"
  })
);

function calculateSize(size: "md" | "sm" | "lg") {
  let props = {
    width: "fit-content",
    height: "2.5rem",
    minWidth: "2.5rem"
  };
  switch (size) {
  case "lg":
    props = {
      ...props,
      height: "3.125rem",
      minWidth: "6.75rem"
    };
    break;
  case "sm":
    props = {
      ...props,
      height: "2rem",
      minWidth: "5.4rem"
    };
    break;

  default:
    break;
  }
  return props;
}


const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip hidden={ false } { ...props } classes={ { popper: className } } />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 600
  }
}));

const ButtonPrimary = ({
  text,
  buttonSize = "md",
  tooltipMessage,
  selfPosition = "auto",
  children,
  sxTooltip,
  boxTooltipProps,
  ...props
}: ButtonPropsRestyle) => {
  const theme = useTheme();

  const message = tooltipMessage ?
    <Box sx={ { ...boxTooltipProps, display: "flex", gap: 1 } }>
      <CircleAlert style={ { width: "1rem", height: "1rem" } } />
      <Typography variant="caption">{ tooltipMessage }</Typography>
    </Box> : null;

  const button = (
    <Button
      { ...props }
      sx={ {
        ...props.sx,
        ...calculateSize(buttonSize),
        alignSelf: selfPosition,
        textTransform: "none",
        display: "inline-flex",
        height: buttonSize === "md" ? "2.5rem" : "3.125rem",
        padding: "0 1rem",
        justifyContent: "center",
        alignItems: "center",
        gap: "0.5rem",
        flexShrink: 0,
        borderRadius: "0.5rem",
        backgroundColor: theme.customColors.backgroundPrimaryCta,
        color: theme.customColors.textPrimaryCta,
        textAlign: "center",
        "&:hover": {
          backgroundColor: theme.customColors.backgroundPrimaryCtaHover
        },
        "&:focus": {
          border: `0.13rem solid ${theme.customColors.borderPrimaryCtaHover}`
        },
        "&:active": {
          backgroundColor: theme.customColors.backgroundPrimaryCtaHover,
          border: `0.13rem solid ${theme.customColors.backgroundPrimaryCta}`
        },
        "&.Mui-disabled": {
          opacity: 0.3,
          backgroundColor: theme.customColors.backgroundDisabled,
          color: theme.customColors.textDisabled
        }
      } }
    >
      { text ? <StyledTypography sx={ { whiteSpace: "nowrap" } } variant="h3" buttonSize={ buttonSize }>{ text }</StyledTypography> : children }
    </Button>
  );

  // If tooltipMessage exists, wrap in CustomTooltip
  if (message) {
    return (
      <CustomTooltip
        sx={ sxTooltip }
        title={ message }
      >
        { /* Wrap disabled button in span to make tooltip work */ }
        { props.disabled ? <span>{ button }</span> : button }
      </CustomTooltip>
    );
  }

  // Return button without tooltip
  return button;
};

export const ButtonSecondary = ({
  text,
  children,
  buttonSize = "md",
  selfPosition = "auto",
  tooltipMessage: _tooltipMessage,
  sxTooltip: _sxTooltip,
  boxTooltipProps: _boxTooltipProps,
  typology: _typology,
  active: _active,
  ...props
}: ButtonPropsRestyle) => {
  const theme = useTheme();

  return (
    <Button
      { ...props }
      sx={ {
        ...props.sx,
        ...calculateSize(buttonSize),
        alignSelf: selfPosition,
        textTransform: "none",
        display: "inline-flex",
        padding: "0.78rem 1rem",
        justifyContent: "center",
        alignItems: "center",
        gap: "0.65625rem",
        flexShrink: 0,
        borderRadius: "0.5rem",
        backgroundColor: theme.customColors.backgroundPrimary,
        border: `0.13rem solid ${theme.customColors.textPrimary}`,
        color: theme.customColors.textPrimary,
        "&:hover": {
          backgroundColor: theme.customColors.backgroundSecondaryCtaHover
        },
        "&:focus": {
          border: `0.13rem solid ${theme.customColors.backgroundPrimaryCta}`
        },
        "&:active": {
          border: `0.13rem solid ${theme.customColors.backgroundPrimaryCta}`
        },
        "&.Mui-disabled": {
          opacity: 0.3,
          color: theme.customColors.textPrimary,
          backgroundColor: theme.customColors.backgroundDisabledAlternative,
          border: `0.13rem solid ${theme.customColors.textPrimary}`
        },
        "& a": {
          display: "flex",
          alignItems: "center"
        }
      } }
    >
      { text ?
        <StyledTypography variant="h3"> { text } </StyledTypography> :
        children   }
    </Button>
  );
};

export const IconOnly = ({
  buttonSize = "md",
  children,
  text: _text,
  tooltipMessage: _tooltipMessage,
  sxTooltip: _sxTooltip,
  boxTooltipProps: _boxTooltipProps,
  typology: _typology,
  active: _active,
  selfPosition: _selfPosition,
  ...props
}: ButtonPropsRestyle) => {
  const theme = useTheme();

  function calculateSizeIconOnly() {
    let props = {
      height: "2.5rem",
      width: "2.5rem"
    };
    switch (buttonSize) {
    case "lg":
      props = {
        ...props,
        height: "3.125rem",
        width: "3.125rem"
      };
      break;
    case "sm":
      props = {
        ...props,
        height: "2rem",
        width: "2rem"
      };
      break;

    default:
      break;
    }
    return props;
  }

  return (
    <Button
      { ...props }
      sx={ {
        ...props.sx,
        minWidth: "auto",
        textTransform: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        ...calculateSizeIconOnly(),
        borderRadius: "0.5rem",
        flexShrink: 0,
        backgroundColor: theme.customColors.backgroundPrimary,
        border: `0.13rem solid ${theme.customColors.textPrimary}`,
        transition: "all 0.3s ease",
        "& svg": {
          transition: "color 0.3s ease",
          width: buttonSize === "md" ? "1rem !important" : "1.25rem",
          height: buttonSize === "md" ? "1rem !important" : "1.25rem"
        },
        "& .MuiCircularProgress-root": {
          width: buttonSize === "md" ? "1rem !important" : "1.25rem",
          height: buttonSize === "md" ? "1rem !important" : "1.25rem"
        },
        "&:hover": {
          backgroundColor: theme.customColors.backgroundSecondaryCtaHover
        },
        "&:focus": {
          borderColor: theme.customColors.backgroundPrimaryCta
        },
        "&.Mui-disabled": {
          // backgroundColor: theme.customColors.backgroundDisabledAlternative,
          // color: alpha(theme.customColors.textPrimary, 0.6),
          // border: `0.13rem solid ${alpha(theme.customColors.textPrimary, 0.6)}`,
          opacity: 0.3,
          color: theme.customColors.textPrimary,
          backgroundColor: theme.customColors.backgroundDisabledAlternative,
          border: `0.13rem solid ${theme.customColors.textPrimary}`,
          "& svg": {
            color: theme.customColors.textPrimary
          }
        }
      } }
    >
      { children }
    </Button>
  );
};


export const ButtonTertiary = ({
  children,
  text,
  buttonSize = "md",
  selfPosition = "auto",
  tooltipMessage: _tooltipMessage,
  sxTooltip: _sxTooltip,
  boxTooltipProps: _boxTooltipProps,
  typology: _typology,
  active: _active,
  ...props
}: ButtonPropsRestyle) => {
  const theme = useTheme();

  return (
    <Button
      { ...props }
      sx={ {
        ...props.sx,
        textTransform: "none",
        textDecoration: "underline",
        textUnderlineOffset: "5px",
        ...calculateSize(buttonSize),
        alignSelf: selfPosition,
        backgroundColor: "transparent",
        border: "1px solid transparent",
        color: theme.customColors.systemSecondary01,
        transition: "all 0.2s ease",
        "& svg": {
          color:theme.customColors.systemSecondary01,
          stroke:theme.customColors.systemSecondary01,
          transition: "all 0.2s ease"
        } ,
        "&:hover": {
          color: theme.customColors.textPrimary,
          textDecoration: "underline",
          background: "transparent",
          textUnderlineOffset: "5px"  ,
          transition: "all 0.2s ease",
          "& svg": {
            color:theme.customColors.textPrimary,
            stroke:theme.customColors.textPrimary,
            transition: "all 0.2s ease"
          }
        },
        "&:active": {
          color: theme.customColors.textPrimary,
          borderColor: theme.customColors.borderPrimaryCta,
          textDecoration: "underline",
          textUnderlineOffset: "5px"
        },
        "&:focus": {
          borderColor: theme.customColors.backgroundPrimaryCta
        },
        "&.Mui-disabled": {
          color: alpha(theme.customColors.textPrimary, 0.3),
          "& svg": {
            color: alpha(theme.customColors.textPrimary, 0.3),
            stroke: alpha(theme.customColors.textPrimary, 0.3)
          }
        }
      } }
    >
      { text ?
        <StyledTypography variant="h3" buttonSize={ buttonSize }>
          { text }
        </StyledTypography>
        : children }
    </Button>
  );
};

export const ButtonLinkRouter = ({
  children,
  buttonSize = "md",
  selfPosition = "auto",
  text: _text,
  tooltipMessage: _tooltipMessage,
  sxTooltip: _sxTooltip,
  boxTooltipProps: _boxTooltipProps,
  typology: _typology,
  active: _active,
  ...props
}: ButtonPropsRestyle) => {
  const theme = useTheme();

  return (
    <Button
      { ...props }
      sx={ {
        ...props.sx,
        textTransform: "none",
        textDecoration: "underline",
        textUnderlineOffset: "5px",
        padding:0,
        ...calculateSize(buttonSize),
        alignSelf: selfPosition,
        backgroundColor: "transparent",
        border: "1px solid transparent",
        color: theme.customColors.systemSecondary01,
        transition: "all 0.2s ease",
        "& svg": {
          color:theme.customColors.systemSecondary01,
          stroke:theme.customColors.systemSecondary01,
          transition: "all 0.2s ease"
        } ,
        "&:hover": {
          color: theme.customColors.textPrimary,
          textDecoration: "underline",
          background: "transparent",
          textUnderlineOffset: "5px"  ,
          transition: "all 0.2s ease",
          "& svg": {
            color:theme.customColors.textPrimary,
            stroke:theme.customColors.textPrimary,
            transition: "all 0.2s ease"
          }
        },
        "&:active": {
          color: theme.customColors.textPrimary,
          borderColor: theme.customColors.borderPrimaryCta,
          textDecoration: "underline",
          textUnderlineOffset: "5px"
        },
        "&:focus": {
          borderColor: theme.customColors.backgroundPrimaryCta
        },
        "&.Mui-disabled": {
          color: alpha(theme.customColors.textPrimary, 0.3),
          "& svg": {
            color: alpha(theme.customColors.textPrimary, 0.3),
            stroke: alpha(theme.customColors.textPrimary, 0.3)
          }
        }
      } }
    >
      { children }
    </Button>
  );
};

export const ButtonFilter = ({
  children,
  text: _text,
  buttonSize: _buttonSize,
  selfPosition: _selfPosition,
  tooltipMessage: _tooltipMessage,
  sxTooltip: _sxTooltip,
  boxTooltipProps: _boxTooltipProps,
  typology: _typology,
  active: _active,
  ...props
}: ButtonPropsRestyle) => {
  const theme = useTheme();
  return (
    <Button
      { ...props }
      sx={ {
        minWidth: "auto",
        textTransform: "none",
        display: "inline-flex",
        alignItems: "center",
        backgroundColor: "transparent",
        height: "2rem",
        justifyContent: "center",
        width: "auto",
        borderRadius: "0.5rem",
        flexShrink: 0,
        border: `1px solid ${theme.customColors.accentVariantA}`,
        color: theme.customColors.accentVariantA,
        transition: "all 0.3s ease",
        gap: 1,
        "&:hover": {
          backgroundColor: theme.customColors.systemSecondary01Hover,
          border: `1px solid ${theme.customColors.borderPrimaryCta}`,
          color: theme.customColors.textPrimaryCta
        }

      } }
    >
      { children }
    </Button>
  );
};


export const ButtonDigited = ({
  typology,
  ...props
}: ButtonPropsRestyle & { typology: string }) => {

  switch (typology) {
  case "primary":
    return ButtonPrimary({ ...props });
  case "secondary":
    return ButtonSecondary({ ...props });
  case "icononly":
    return IconOnly({ ...props });
  case "tertiary":
    return ButtonTertiary({ ...props });
  case "linkState":
    return ButtonLinkRouter({ ...props });
  case "filter":
    return ButtonFilter({ ...props });
  default:
    return;
  }
};
